import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

export default function InsightArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/insights/article/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="insight-missing">
        <h1 className="font-display text-3xl font-black text-zinc-950">That article isn't here any more.</h1>
        <p className="text-sm text-zinc-500 mt-3">Browse the latest guidance on the insights page instead.</p>
        <Link to="/resources" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-blue-700 hover:text-blue-900">
          <ArrowLeft className="h-4 w-4" /> Career &amp; market insights
        </Link>
      </main>
    );
  }

  if (!article) {
    return <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20"><div className="h-64 rounded-md bg-zinc-100 animate-pulse" /></main>;
  }

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    "headline": article.title, "description": article.summary,
    "datePublished": article.published_at,
    "author": { "@type": "Organization", "name": "HiredToday" },
    "publisher": { "@type": "Organization", "name": "HiredToday" },
  };

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="insight-article">
      <Seo title={`${article.title} | HiredToday`} description={article.summary} path={`/insights/${slug}`} jsonLd={jsonLd} />
      <Link to="/resources" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900" data-testid="insight-back">
        <ArrowLeft className="h-4 w-4" /> Back to insights
      </Link>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mt-8 mb-3">{article.category}</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950">{article.title}</h1>
      <p className="text-xs text-zinc-400 mt-4" data-testid="insight-date">
        By the HiredToday team
        {article.published_at && ` · ${new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`}
      </p>
      <div className="mt-10 space-y-5 text-base text-zinc-700 leading-relaxed" data-testid="insight-body">
        {(article.body || "").split(/\n\s*\n/).filter(Boolean).map((para, i) => (
          <p key={`${i}-${para.slice(0, 24)}`}>{para}</p>
        ))}
      </div>
    </main>
  );
}
